/**
 * Admin dashboard for the `travelbio_destinations` catalog.
 *
 * URL: /admin/cards  (gated by <AdminProtected>)
 *
 * A searchable, sortable, filterable table of every travelbio card.
 * Clicking a row opens the shared <AdminCardEditModal> for full edit,
 * upload, or delete. The modal's `onSaved` callback refreshes the row
 * in place — no full-table reload needed.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Pencil,
  Image as ImageIcon, ExternalLink, Plus,
} from "lucide-react";
import { api } from "@/lib/api";
import AdminCardEditModal from "@/components/AdminCardEditModal";
import ReviewerLockdown from "@/components/ReviewerLockdown";
import { useAuth } from "@/context/AuthContext";

const PAGE_SIZE = 25;

const SORT_COLS = [
  { key: "name", label: "Name" },
  { key: "content_type", label: "Type" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "image_status", label: "Image" },
  { key: "updated_at", label: "Updated" },
];

const IMG_STATUS_STYLES = {
  manually_verified: "bg-tb-gold/25 text-tb-gold border-tb-gold",
  auto_matched: "bg-tb-blue/25 text-tb-blue border-tb-blue",
  placeholder: "bg-tb-navy-deep text-tb-cream/60 border-tb-cream/25",
};

export default function AdminCardsPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [q, setQ] = useState("");
  const [qDeb, setQDeb] = useState("");
  const [contentType, setContentType] = useState("");
  const [primaryFilter, setPrimaryFilter] = useState("");
  const [imageStatus, setImageStatus] = useState("");
  const [state, setState] = useState("");
  const [sort, setSort] = useState("name");
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(1);

  const [facets, setFacets] = useState({
    content_types: [], primary_filters: [], states: [],
    countries: [], image_statuses: [],
  });

  const [editingId, setEditingId] = useState(null);

  // Debounce search input so we're not hammering the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setQDeb(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  // Reset to page 1 whenever any filter changes.
  useEffect(() => {
    setPage(1);
  }, [qDeb, contentType, primaryFilter, imageStatus, state, sort, order]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        limit: PAGE_SIZE, page, sort, order,
      };
      if (qDeb) params.q = qDeb;
      if (contentType) params.content_type = contentType;
      if (primaryFilter) params.primary_filter = primaryFilter;
      if (imageStatus) params.image_status = imageStatus;
      if (state) params.state = state;
      const { data } = await api.get("/admin/travelbio-destinations", { params });
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error("[AdminCardsPage] load failed", e);
      setError(e.response?.data?.detail || e.message || "Load failed");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [qDeb, contentType, primaryFilter, imageStatus, state, sort, order, page]);

  useEffect(() => { load(); }, [load]);

  // Facets load once — they're cheap and cover the whole catalog.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/admin/travelbio-destinations/facets");
        if (alive) setFacets(data);
      } catch (e) {
        console.warn("[AdminCardsPage] facets load failed", e);
      }
    })();
    return () => { alive = false; };
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const toggleSort = (col) => {
    if (sort === col) {
      setOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSort(col);
      setOrder("asc");
    }
  };

  const onCardSaved = useCallback((updated) => {
    // Splice the fresh doc into the visible rows so the table reflects
    // the change without waiting for a full reload.
    setRows((prev) =>
      prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)),
    );
  }, []);

  const summary = useMemo(() => {
    const start = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const end = Math.min(page * PAGE_SIZE, total);
    return `${start}\u2013${end} of ${total}`;
  }, [page, total]);

  return (
    <div
      data-testid="admin-cards-page"
      className={"min-h-screen bg-tb-navy-deep text-tb-cream p-4 sm:p-6 " + (user?.role === "reviewer" ? "reviewer-locked" : "")}
    >
      <ReviewerLockdown />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <h1 className="font-serif-it text-3xl sm:text-4xl text-tb-gold">
              Cards
            </h1>
            <p className="font-hand text-tb-cream/60 text-sm mt-1">
              travelbio catalog — {total} rows
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="admin-cards-add-new"
              onClick={() => setEditingId("__new__")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-tb-gold text-tb-navy border-2 border-tb-gold font-hand text-sm tb-tap"
            >
              <Plus className="inline w-4 h-4" /> add destination
            </button>
            <Link
              to="/admin"
              className="px-3 py-1.5 rounded-full bg-tb-navy border-2 border-tb-cream/30 text-tb-cream font-hand text-sm tb-tap"
              data-testid="admin-cards-back-to-admin"
            >
              <ChevronLeft className="inline w-4 h-4" /> admin
            </Link>
          </div>
        </div>

        {/* Filter bar */}
        <div className="bg-tb-navy border-2 border-tb-gold/40 rounded-xl p-3 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
            <label className="md:col-span-2 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-tb-navy/50" />
              <input
                data-testid="admin-cards-search"
                type="text"
                placeholder="search by name or city…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="w-full pl-9 pr-3 py-2 rounded-lg bg-tb-cream text-tb-navy border-2 border-tb-cream-3 focus:border-tb-gold outline-none font-ui text-sm"
              />
            </label>
            <select
              data-testid="admin-cards-filter-content-type"
              value={contentType}
              onChange={(e) => setContentType(e.target.value)}
              className="px-3 py-2 rounded-lg bg-tb-cream text-tb-navy border-2 border-tb-cream-3 font-ui text-sm"
            >
              <option value="">All content types</option>
              {(facets.content_types || []).map((ct) => (
                <option key={ct} value={ct}>{ct}</option>
              ))}
            </select>
            <select
              data-testid="admin-cards-filter-image-status"
              value={imageStatus}
              onChange={(e) => setImageStatus(e.target.value)}
              className="px-3 py-2 rounded-lg bg-tb-cream text-tb-navy border-2 border-tb-cream-3 font-ui text-sm"
            >
              <option value="">All image statuses</option>
              {(facets.image_statuses || []).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <select
              data-testid="admin-cards-filter-state"
              value={state}
              onChange={(e) => setState(e.target.value)}
              className="px-3 py-2 rounded-lg bg-tb-cream text-tb-navy border-2 border-tb-cream-3 font-ui text-sm"
            >
              <option value="">All states</option>
              {(facets.states || []).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="bg-tb-navy border-2 border-tb-gold/30 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-tb-navy-deep border-b-2 border-tb-gold/30">
                <tr>
                  <th className="w-16 px-3 py-2 text-left font-hand text-tb-gold text-xs">
                    Photo
                  </th>
                  {SORT_COLS.map(({ key, label }) => (
                    <th
                      key={key}
                      className="px-3 py-2 text-left font-hand text-tb-gold text-xs cursor-pointer select-none"
                      onClick={() => toggleSort(key)}
                      data-testid={`admin-cards-sort-${key.replace(/_/g, "-")}`}
                    >
                      <span className="inline-flex items-center gap-1">
                        {label}
                        {sort === key ? (
                          order === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : null}
                      </span>
                    </th>
                  ))}
                  <th className="w-16 px-3 py-2 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={SORT_COLS.length + 2}
                      className="p-8 text-center font-hand text-tb-cream/50">
                      ~ loading ~
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={SORT_COLS.length + 2}
                      className="p-8 text-center font-hand text-tb-coral"
                      data-testid="admin-cards-error">
                      {error}
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={SORT_COLS.length + 2}
                      className="p-8 text-center font-hand text-tb-cream/50">
                      no cards match those filters
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr
                      key={r.id}
                      data-testid={`admin-cards-row-${r.id}`}
                      onClick={() => setEditingId(r.id)}
                      className="border-t border-tb-cream/10 hover:bg-tb-navy-deep/60 cursor-pointer transition"
                    >
                      <td className="px-3 py-2">
                        <div className="w-10 h-10 rounded overflow-hidden bg-tb-navy-deep border border-tb-cream/20">
                          {r.featured_image ? (
                            <img
                              src={r.featured_image}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <ImageIcon className="w-4 h-4 m-auto mt-3 text-tb-cream/40" />
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-ui text-tb-cream">
                          {r.name || <em className="text-tb-cream/40">(no name)</em>}
                        </div>
                        <div className="font-hand text-tb-cream/40 text-[11px]">
                          {r.id}
                        </div>
                      </td>
                      <td className="px-3 py-2 font-hand text-tb-cream/80">
                        {r.content_type || "-"}
                      </td>
                      <td className="px-3 py-2 font-hand text-tb-cream/80">
                        {r.city || "-"}
                      </td>
                      <td className="px-3 py-2 font-hand text-tb-cream/80">
                        {r.state || "-"}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full border font-hand text-[11px] ${
                            IMG_STATUS_STYLES[r.image_status] ||
                            IMG_STATUS_STYLES.placeholder
                          }`}
                        >
                          {r.image_status || "placeholder"}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-hand text-tb-cream/60 text-[11px]">
                        {r.updated_at
                          ? new Date(r.updated_at).toLocaleDateString()
                          : "-"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingId(r.id);
                          }}
                          data-testid={`admin-cards-edit-${r.id}`}
                          className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-tb-gold/20 border border-tb-gold text-tb-gold hover:bg-tb-gold hover:text-tb-navy tb-tap"
                          aria-label="edit"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {total > 0 && (
          <div className="flex items-center justify-between mt-3">
            <div className="font-hand text-tb-cream/60 text-sm"
                 data-testid="admin-cards-summary">
              {summary}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="admin-cards-prev-page"
                className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-tb-navy border-2 border-tb-cream/30 text-tb-cream disabled:opacity-40 tb-tap"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="font-hand text-tb-cream/70 text-sm min-w-[80px] text-center">
                page {page} of {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                data-testid="admin-cards-next-page"
                className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-tb-navy border-2 border-tb-cream/30 text-tb-cream disabled:opacity-40 tb-tap"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Shared edit modal — `__new__` sentinel triggers create mode */}
      <AdminCardEditModal
        cardId={editingId === "__new__" ? null : editingId}
        open={!!editingId}
        onClose={() => setEditingId(null)}
        onSaved={onCardSaved}
      />
    </div>
  );
}
